// Refactored from https://github.com/Azure-Samples/ms-identity-javascript-react-tutorial/blob/main/1-Authentication/1-sign-in/SPA/src/authConfig.js

import { IPublicClientApplication } from "@azure/msal-browser";

const BACKEND_URI = ((import.meta as any)?.env?.VITE_BACKEND_URI ?? "").replace(/\/$/, "");

const graphMeEndpoint = "https://graph.microsoft.com/v1.0/me";
const graphPhotoEndpoint = "https://graph.microsoft.com/v1.0/me/photo/$value";

interface AuthSetup {
    // Set to true if login elements should be shown in the UI
    useLogin: boolean;
    // Set to true if access control is enforced by the application
    requireAccessControl: boolean;
    // Set to true if the application allows unauthenticated access (only applies for documents without access control)
    enableUnauthenticatedAccess: boolean;
    // Indicates whether MSAL-based auth should be enabled
    msalEnabled?: boolean;
    // Indicates whether the custom OAuth flow is enabled
    customAuthEnabled?: boolean;
    /**
     * Configuration object to be passed to MSAL instance on creation.
     * For a full list of MSAL.js configuration parameters, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
     */
    msalConfig: {
        auth: {
            clientId: string; // Client app id used for login
            authority: string; // Directory to use for login https://learn.microsoft.com/entra/identity-platform/msal-client-application-configuration#authority
            redirectUri: string; // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
            postLogoutRedirectUri: string; // Indicates the page to navigate after logout.
            navigateToLoginRequestUrl: boolean; // If "true", will navigate back to the original request location before processing the auth code response.
        };
        cache: {
            cacheLocation: string; // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
            storeAuthStateInCookie: boolean; // Set this to "true" if you are having issues on IE11 or Edge
        };
    };
    loginRequest: {
        /**
         * Scopes you add here will be prompted for user consent during sign-in.
         * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
         * For more information about OIDC scopes, visit:
         * https://learn.microsoft.com/entra/identity-platform/permissions-consent-overview#openid-connect-scopes
         */
        scopes: Array<string>;
    };
    tokenRequest: {
        scopes: Array<string>;
    };
}

// Fetch the auth setup JSON data from the API if not already cached
async function fetchAuthSetup(): Promise<AuthSetup> {
    const response = await fetch(`${BACKEND_URI}/auth_setup`);
    if (!response.ok) {
        throw new Error(`auth setup response was not ok: ${response.status}`);
    }
    return await response.json();
}

async function acquireToken(client: IPublicClientApplication): Promise<string | null> {
    const account = client.getActiveAccount();
    if (!account) return null;

    try {
        const response = await client.acquireTokenSilent({
            ...loginRequest,
            account
        });

        return response.accessToken;
    } catch (err) {
        console.error("acquireToken failed:", err);
        return null;
    }
}

async function fetchGraphData<T = any>(url: string, accessToken: string): Promise<T | null> {
    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        console.warn(`Graph fetch failed for ${url}`);
        return null;
    }

    try {
        if (url.endsWith("/$value")) {
            const blob = await response.blob();
            return URL.createObjectURL(blob) as unknown as T;
        } else {
            return await response.json();
        }
    } catch (err) {
        console.error(`Failed to parse response from ${url}`, err);
        return null;
    }
}

const authSetup = await fetchAuthSetup();

export const useLogin = Boolean(authSetup.useLogin);
export const customAuthEnabled = Boolean(authSetup.customAuthEnabled);

// Check if we should use MSAL (true for Azure AD, false for custom OAuth)
const msalClientId = authSetup.msalConfig?.auth?.clientId ?? "";
const msalEnabledByServer = typeof authSetup.msalEnabled === "boolean" ? authSetup.msalEnabled : undefined;
export const useMsalAuth =
    msalEnabledByServer !== undefined
        ? msalEnabledByServer
        : Boolean(
              authSetup.useLogin &&
              !customAuthEnabled &&
              msalClientId &&
              msalClientId !== "00000000-0000-0000-0000-000000000000"
          );

export const requireAccessControl = authSetup.requireAccessControl;

export const enableUnauthenticatedAccess = authSetup.enableUnauthenticatedAccess;

export const requireLogin = requireAccessControl && !enableUnauthenticatedAccess;

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = authSetup.msalConfig;

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://learn.microsoft.com/entra/identity-platform/permissions-consent-overview#openid-connect-scopes
 */
export const loginRequest = authSetup.loginRequest;

const tokenRequest = authSetup.tokenRequest;

// Build an absolute redirect URI using the current window's location and the relative redirect URI from auth setup
export const getRedirectUri = () => {
    return window.location.origin + authSetup.msalConfig.auth.redirectUri;
};

/**
 * Determines if the user is logged in either via the MSAL public client application or the app services login.
 * @param {IPublicClientApplication | undefined} client - The MSAL public client application instance, or undefined if not available.
 * @returns {Promise<boolean>} A promise that resolves to true if the user is logged in, false otherwise.
 */
export const checkLoggedIn = async (client: IPublicClientApplication | undefined): Promise<boolean> => {
    if (client) {
        const activeAccount = client.getActiveAccount();
        if (activeAccount) {
            return true;
        }
    }

    return false;
};

// Get an access token for use with the API server.
// ID token received when logging in may not be used for this purpose because it has the incorrect audience
// Use the access token from app services login if available
export const getToken = async (client: IPublicClientApplication): Promise<string | undefined> => {
    return client
        .acquireTokenSilent({
            ...tokenRequest,
            redirectUri: getRedirectUri()
        })
        .then(r => r.accessToken)
        .catch(error => {
            console.log(error);
            return undefined;
        });
};

/**
 * Retrieves the username of the active account.
 * If no active account is found, attempts to retrieve the username from the app services login token if available.
 * @param {IPublicClientApplication} client - The MSAL public client application instance.
 * @returns {Promise<string | null>} The username of the active account, or null if no username is found.
 */
export const getUsername = async (client: IPublicClientApplication): Promise<string | null> => {
    const activeAccount = client.getActiveAccount();
    if (activeAccount) {
        return activeAccount.username;
    }

    return null;
};

/**
 * Retrieves the token claims of the active account.
 * If no active account is found, attempts to retrieve the token claims from the app services login token if available.
 * @param {IPublicClientApplication} client - The MSAL public client application instance.
 * @returns {Promise<Record<string, unknown> | undefined>} A promise that resolves to the token claims of the active account, the user claims from the app services login token, or undefined if no claims are found.
 */
export const getTokenClaims = async (client: IPublicClientApplication): Promise<Record<string, unknown> | undefined> => {
    const activeAccount = client.getActiveAccount();
    if (activeAccount) {
        return activeAccount.idTokenClaims;
    }

    return undefined;
};

/**
 * Retrieves the display name of the active account.
 * If no active account is found, attempts to retrieve the display name from the app services login token if available.
 * @param {IPublicClientApplication} client - The MSAL public client application instance.
 * @returns {Promise<string | null>} The display name of the active account, or null if no display name is found.
 */
export const getDisplayName = async (client: IPublicClientApplication): Promise<string | null> => {
    const token = await acquireToken(client);
    if (token) {
        const profile = await fetchGraphData<{ displayName?: string }>(graphMeEndpoint, token);
        if (profile?.displayName) return profile.displayName;
    }

    return null;
};

/**
 * Retrieves the profile photo URL of the active account.
 * If no active account is found, attempts to retrieve the profile photo URL from the app services login token if available.
 * @param {IPublicClientApplication} client - The MSAL public client application instance.
 * @returns {Promise<string | null>} The profile photo URL of the active account, or null if no URL is found.
 */
export const getProfilePhotoUrl = async (client: IPublicClientApplication): Promise<string | null> => {
    const token = await acquireToken(client);
    if (!token) return null;

    return await fetchGraphData<string>(graphPhotoEndpoint, token);
};
